import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useNotifications } from '@/hooks/useNotifications';
import { Badge } from '@/components/common/Badge';
import { isPathActive, type RibbonGroup, type RibbonItem } from './navConfig';

// Presentational ribbon band: renders one tab's groups as large command buttons
// with a group label beneath each cluster and dividers between groups.
export default function RibbonBand({ groups }: { groups: RibbonGroup[] }) {
  const location = useLocation();
  const { unreadCount } = useNotifications();

  const CommandButton = ({ item }: { item: RibbonItem }) => {
    const active = isPathActive(location.pathname, item.href);
    const showBadge = item.href === '/notifications' && unreadCount > 0;
    return (
      <Link
        to={item.href}
        className={cn(
          'relative flex flex-col items-center justify-center gap-1 w-[72px] px-1 py-1.5 rounded-lg transition-all',
          active
            ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
            : 'text-foreground/80 hover:bg-accent hover:text-foreground'
        )}
      >
        <item.icon className="h-6 w-6" strokeWidth={1.75} />
        <span className="text-[11px] leading-tight text-center line-clamp-1">{item.name}</span>
        {showBadge && (
          <Badge
            variant="destructive"
            className="absolute top-0.5 right-2 h-4 min-w-4 flex items-center justify-center p-0 text-[10px]"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <div className="flex items-stretch h-full gap-1 px-3">
      {groups.map((group) => (
        <div
          key={group.label}
          className="flex flex-col justify-between py-1.5 px-2 border-r border-border/60 last:border-r-0"
        >
          <div className="flex items-start gap-0.5 flex-1">
            {group.items.map((item) => (
              <CommandButton key={item.href} item={item} />
            ))}
          </div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 text-center mt-0.5">
            {group.label}
          </span>
        </div>
      ))}
    </div>
  );
}
